<template>
<b-container fluid class="mt-4 px-3">
    <b-card>
      <!-- Header -->
      <b-row class="mb-3 align-items-center">
        <b-col cols="6">
          <h3 class="mb-0">Listado de artículos</h3>
        </b-col>

        <b-col cols="6" class="text-right">
          <b-button variant="success" @click="showCreate">
            <b-icon icon="plus-circle" aria-hidden="true" class="mr-1" /> Nuevo
            artículo
          </b-button>
        </b-col>
      </b-row>

      <!-- Filtros -->
      <b-row class="mb-2">
        <b-col md="4" class="mb-2">
          <b-input-group>
            <b-form-input
              v-model.trim="q"
              placeholder="Buscar por descripción, proveedor o categoría..."
              @keyup.enter="applySearch"
            />
            <b-input-group-append>
              <b-button @click="applySearch">Buscar</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>

        <b-col md="3" class="mb-2">
          <b-form-select
            v-model="filterCategory"
            :options="categoryFilterOptions"
            placeholder="Filtrar por categoría"
          />
        </b-col>

        <b-col md="2" class="mb-2">
          <b-form-select v-model.number="perPage" :options="perPageOptions" />
        </b-col>

        <b-col md="3" class="text-right mb-2">
          <small class="text-muted">Mostrando {{ itemsCountText }}</small>
        </b-col>
      </b-row>

      <!-- Tabla -->
      <b-row>
        <b-col>
          <b-table
            :items="pagedItems"
            :fields="fields"
            :busy="loading"
            small
            responsive="sm"
            hover
            striped
            stacked="md"
            show-empty
            @row-clicked="viewItem"
          >
            <template #cell(stock)="row">
              <b-badge :variant="stockVariant(row.item)">{{
                row.item.stock
              }}</b-badge>
              <small class="text-muted ml-2"
                >min: {{ row.item.stock_min }}</small
              >
            </template>

            <template #cell(price)="row">
              {{ formatCurrency(row.item.price) }}
            </template>

            <template #cell(cost)="row">
              {{ formatCurrency(row.item.cost) }}
            </template>

            <template #cell(category)="row">
              <span v-if="row.item.category">{{ row.item.category.name }}</span>
              <small v-else class="text-muted">-</small>
            </template>

            <template #cell(actions)="row">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click.stop="showEdit(row.item)"
              >
                <b-icon icon="pencil-square" />
              </b-button>
              <!--b-button
                size="sm"
                variant="outline-danger"
                @click.stop="confirmDelete(row.item)"
              >
                <b-icon icon="trash" />
              </b-button-->
            </template>

            <template #empty>
              <div class="text-center text-muted py-4">
                <div v-if="!loading">
                  <p class="mb-1">No hay artículos para mostrar.</p>
                  <small
                    >Podés crear uno nuevo o usar los datos de ejemplo para
                    probar la interfaz.</small
                  >
                </div>
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Paginación -->
      <b-row class="mt-3 align-items-center">
        <b-col>
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredItems.length"
            :per-page="perPage"
            align="left"
            size="sm"
          />
        </b-col>

        <b-col class="text-right">
          <small class="text-muted"
            >Página {{ currentPage }} / {{ totalPages }}</small
          >
        </b-col>
      </b-row>
    </b-card>

    <!-- Modal Crear/Editar Artículo -->
    <article-form-modal
      ref="modal"
      @saved="onSaved"
      :article="selected"
      :categories="categories"
    />

    <!-- Confirmar eliminación -->
    <b-modal
      id="confirm-delete"
      ref="confirmDeleteModal"
      hide-footer
      title="Confirmar eliminación"
    >
      <div class="my-3">
        <p>
          ¿Estás seguro que querés eliminar el artículo
          <strong>{{ deleteTarget?.description }}</strong
          >?
        </p>
        <p class="text-danger small">Esta acción no se puede deshacer.</p>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="hideConfirm" :disabled="deleting"
          >Cancelar</b-button
        >
        <b-button variant="danger" @click="deleteItem" :disabled="deleting">
          <b-spinner v-if="deleting" small class="mr-2" />Eliminar
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import ArticleFormModal from "./ArticleFormModal.vue";

export default {
  components: { ArticleFormModal },
  data() {
    return {
      q: "",
      filterCategory: null,
      perPage: 30,
      perPageOptions: [
        { value: 5, text: "5 por página" },
        { value: 8, text: "8 por página" },
        { value: 15, text: "15 por página" },
      ],
      currentPage: 1,
      fields: [
        { key: "code", label: "CODIGO", thStyle: { width: "50px" } },
        { key: "description", label: "DESCRIPCION", sortable: true , thStyle: { width: "220px" } },
        //{ key: "provider_description", label: "Proveedor" },
        { key: "category", label: "CATEGORIA" , thStyle: { width: "80px" } },
        { key: "stock", label: "STOCK", thStyle: { width: "70px" } },
        { key: "cost", label: "COSTO", thStyle: { width: "80px" } },
        { key: "price", label: "PRECIO", thStyle: { width: "80px" } },
        { key: "actions", label: "", thStyle: { width: "50px" } },
      ],
      selected: null,
      deleteTarget: null,
      deleting: false,
      categories: [],
    };
  },

  computed: {
    rawItems() {
      return this.$store.getters["articles/all"];
    },
    loading() {
      return this.$store.state.articles.loading;
    },
    filteredItems() {
      const q = (this.q || "").toLowerCase().trim();
      return this.rawItems.filter((item) => {
        const byCategory = this.filterCategory
          ? item.category && item.category.id === this.filterCategory
          : true;
        if (!q) return byCategory;
        const inDescription = (item.description || "")
          .toLowerCase()
          .includes(q);
        const inProvider = (item.provider_description || "")
          .toLowerCase()
          .includes(q);
        const inCategory = item.category
          ? (item.category.name || "").toLowerCase().includes(q)
          : false;
        return byCategory && (inDescription || inProvider || inCategory);
      });
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredItems.slice(start, start + this.perPage);
    },
    itemsCountText() {
      return `${this.filteredItems.length} artículos`;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage));
    },
    categoryFilterOptions() {
      return [{ value: null, text: "Todas las categorías" }].concat(
        this.categories.map((c) => ({ value: c.id, text: c.name }))
      );
    },
  },

  methods: {
    applySearch() {
      this.currentPage = 1;
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return (
        "$ " +
        Number(value).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    stockVariant(item) {
      if (!item) return "secondary";
      return item.stock <= item.stock_min ? "danger" : "success";
    },
    showCreate() {
      this.selected = null;
      this.$refs.modal.show();
    },
    showEdit(item) {
      this.selected = Object.assign({}, item);
      this.$refs.modal.show();
    },
    viewItem(item) {
      this.$bvToast.toast(`${item.description} — Stock: ${item.stock}`, {
        title: `Articulo #${item.id}`,
        variant: "info",
        autoHideDelay: 3500,
      });
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.$refs.confirmDeleteModal.show();
    },
    hideConfirm() {
      this.deleteTarget = null;
      this.$refs.confirmDeleteModal.hide();
    },
    async deleteItem() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await this.$store.dispatch("articles/delete", this.deleteTarget.id);
        this.$bvToast.toast("Artículo eliminado correctamente.", {
          title: "Eliminado",
          variant: "success",
          autoHideDelay: 2500,
        });
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("No se pudo eliminar el artículo.", {
          title: "Error",
          variant: "danger",
          autoHideDelay: 3000,
        });
      } finally {
        this.deleting = false;
        this.hideConfirm();
      }
    },
    fetchAll() {
      this.$store.dispatch("articles/fetchAll").catch(console.error);
    },
    onSaved() {
      this.fetchAll();
      this.$bvToast.toast("Artículo guardado correctamente.", {
        title: "Guardado",
        variant: "success",
        autoHideDelay: 2000,
      });
    },
  },

  created() {
    // Cargar categorías desde backend
    this.$store.dispatch("categories/fetchAll").then(() => {
      this.categories = this.$store.getters["categories/all"];
    });

    this.fetchAll();
  },
};
</script>

<style scoped>
h3 {
  font-weight: 600;
}
</style>

